import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { shiftAudioPitch, sameFormat } from "./operations";
import { AudioProcessingProgress, showAudioError, FileSizeNotice } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Disguises a voice by shifting its pitch, useful for anonymizing a recording before sharing it — for example, a whistleblower-style interview, or protecting someone's identity in a recording. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file, choose how much to shift the pitch (lower for a deeper voice, higher for a lighter one), and download the disguised result. The duration stays the same either way.`,
  tip: `This is a real, effective way to make a voice hard to recognize casually, but it isn't forensic-grade anonymization — speech patterns, cadence, and word choice can still potentially identify someone regardless of pitch. For genuinely high-stakes anonymity needs, treat this as one layer, not the only one.`,
};

const PRESET_OPTIONS = [
  { label: "Strongest Deep", value: -8 },
  { label: "Deep Voice", value: -6 },
  { label: "Slightly Lower", value: -3 },
  { label: "Slightly Higher", value: 3 },
  { label: "High Voice", value: 6 },
  { label: "Strongest High", value: 8 },
];

export default function AudioScrambler() {
  const [file, setFile] = useState<File | null>(null);
  const [semitones, setSemitones] = useState([-3]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setPreviewBlob(null);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const scramble = async (preview = false) => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      setStatusMessage("Shifting pitch...");
      // 44100 Hz is a safe, standard assumption for the source sample
      // rate here -- the aresample at the end normalizes the output
      // regardless of what the actual input rate was.
      const fmtOut = sameFormat(file.name);
      const data = await shiftAudioPitch(ffmpeg, `input.${ext}`, `output.${fmtOut.value}`, semitones[0], setProgress, controller.signal, fmtOut.codec, preview ? 8 : 0);

      if (preview) { setPreviewBlob(ffmpegOutputToBlob(data, fmtOut.mime)); toast.success("Preview ready — the first 8 seconds."); }
      else { setResult(ffmpegOutputToBlob(data, fmtOut.mime)); toast.success("Voice disguised!"); }
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file" description="Disguise a voice by shifting its pitch" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <div className="space-y-2 max-w-sm">
            <Label>Pitch Shift: {semitones[0] > 0 ? "+" : ""}{semitones[0]} semitones</Label>
            <Slider value={semitones} onValueChange={(v) => { setSemitones(v); setResult(null); setPreviewBlob(null); }} min={-8} max={8} step={1} />
            <div className="flex flex-wrap gap-1.5">
              {PRESET_OPTIONS.map(p => (
                <Button key={p.label} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setSemitones([p.value]); setResult(null); setPreviewBlob(null); }}>{p.label}</Button>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground max-w-sm">
            A single pitch shift is easy to undo. For a stronger disguise, use a large shift (±6 to ±8), download the result, and run it through this tool again with a different shift.
          </p>
          {previewBlob && !result && <div><p className="text-xs text-muted-foreground mb-1">Preview of the first 8 seconds at {semitones[0] > 0 ? "+" : ""}{semitones[0]} semitones</p><ResultPreview blob={previewBlob} /></div>}
          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && <ResultPreview blob={result} />}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`disguised_${file.name.replace(/\.[^.]+$/, `.${sameFormat(file.name).value}`)}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); setSemitones([-3]); }}>New</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => scramble(true)} disabled={processing || sizeCheck?.severity !== "ok" || semitones[0] === 0}>Preview 8 s</Button>
                <Button onClick={() => scramble(false)} disabled={processing || sizeCheck?.severity !== "ok" || semitones[0] === 0}>{processing ? "Processing..." : "Disguise Voice"}</Button>
                <Button variant="outline" onClick={() => { setFile(null); setSemitones([-3]); }} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
